import TirPmList from '@/components/tir-pm-list/tir-pm-list.vue';
import { ProjectsTasksLayout, QueueLayout } from '@/layouts';
import {
  ProjectNamespacesPage,
  ProjectMainSettingsPage,
  NamespaceMainSettingsPage,
  ProjectPage,
  GroupPage,
  KanbanPage,
  TaskDetailPage,
  NamespaceQueuesPage,
  ProjectTagsSettingPage,
  ProjectSettingsAdminPage,
  NamespaceTypesSettingsPage,
  NamespaceDifficultySettingsPage,
  NamespacePage,
  NamespaceTeamPage,
  NamespaceAdminsPage,
  TasksOverview,
  TeamTasks,
  EmployeeTasksPage,
  TaskEstimatePage,
} from '@/pages';
import { RouteRecordRaw } from 'vue-router';
import {
  checkNamespaceAccess,
  checkNamespaceAdmin,
  checkProjectAdmin,
  checkRoute,
  checkTeam,
} from './middleware';
import {
  MonitoringDays,
  MonitoringProjects,
  Monitorings,
  MonitoringTickets,
} from '@/pages/monitorings';
import ProjectSettingsWeeekPage from '@/pages/project-settings-weeek-page/project-settings-weeek-page.vue';
import WeeekSettingsToken from '@/pages/project-settings-weeek-page/components/weeek-settings-token.vue';
import WeeekStatuses from '@/pages/project-settings-weeek-page/components/weeek-statuses/weeek-statuses.vue';
import WeeekPriorities from '@/pages/project-settings-weeek-page/components/weeek-priorities/weeek-priorities.vue';
import WeeekTypes from '@/pages/project-settings-weeek-page/components/weeek-types/weeek-types.vue';
import WeeekTags from '@/pages/project-settings-weeek-page/components/weeek-tags/weeek-tags.vue';
import { StubPage } from '@/pages/stub-page';
import { UserTasksPage } from '@/pages/user-tasks-page';

const routes: Array<RouteRecordRaw> = [
  {
    name: 'tasks-overview',
    path: '/tasks-overview',
    component: TasksOverview,
  },
  {
    name: 'team-tasks',
    path: 'team-tasks',
    component: TeamTasks,
    props: (to) => ({
      projectId: to?.query?.projectId,
      routerUserId: Number(to?.query?.userId),
      routerProjectId: Number(to?.query?.projectId),
      statusFilter: to?.query?.statusFilter,
    }),
    meta: {
      title: 'Задачи моей команды',
      middleware: [checkTeam],
    },
  },
  {
    name: 'user-tasks',
    path: 'user-tasks/user/:userId/project/:projectId',
    component: UserTasksPage,
    props: (to) => ({
      userId: Number(to?.params?.userId),
      projectId: Number(to?.params?.projectId),
    }),
  },
  {
    name: 'task-estimate',
    path: '/projects/:projectId/namespace/:namespaceId/queue/:queueId/task-estimate/:taskId',
    component: TaskEstimatePage,
    meta: {
      middleware: [checkRoute],
    },
    props: (to) => ({
      projectId: Number(to.params.projectId),
      namespaceId: to.params.namespaceId,
      queueId: to.params.queueId,
      taskId: Number(to.params.taskId),
    }),
  },
  {
    path: ':projectId',
    meta: {
      name: 'Проект',
    },
    redirect: { name: 'tasks' },
    children: [
      {
        name: 'tasks',
        path: '',
        component: ProjectsTasksLayout,
        redirect: { name: 'project' },
        children: [
          {
            name: 'project-stub-page',
            path: 'stub-page/:errorCode?',
            component: StubPage,
          },
          {
            name: 'monitoring',
            path: 'monitoring',
            redirect: { name: 'monitoring-days' },
            component: Monitorings,
            children: [
              {
                name: 'monitoring-days',
                path: 'days',
                component: MonitoringDays,
              },
              {
                name: 'monitoring-tickets',
                path: 'tickets',
                component: MonitoringTickets,
              },
              {
                name: 'monitoring-projects',
                path: 'projects',
                component: MonitoringProjects,
              },
            ],
          },
          {
            name: 'project',
            path: '',
            redirect: { name: 'project-namespaces' },
            meta: { title: '' },
            component: ProjectPage,
            children: [
              {
                name: 'project-namespaces',
                path: 'namespaces',
                component: ProjectNamespacesPage,
              },
              {
                name: 'project-settings',
                path: 'settings',
                component: ProjectMainSettingsPage,
                meta: {
                  title: 'Основная информация',
                  middleware: [checkProjectAdmin],
                },
              },
              {
                name: 'project-admins',
                path: 'admins',
                component: ProjectSettingsAdminPage,
                meta: {
                  title: 'Список администраторов',
                  middleware: [checkProjectAdmin],
                },
              },
              {
                name: 'project-tags',
                path: 'tags',
                component: ProjectTagsSettingPage,
                meta: {
                  title: 'Список тегов',
                  middleware: [checkProjectAdmin],
                },
              },
              {
                name: 'import-weeek',
                path: 'weeek',
                component: ProjectSettingsWeeekPage,
                meta: {
                  middleware: [checkProjectAdmin],
                },
                redirect: { name: 'import-weeek_token' },
                children: [
                  {
                    name: 'import-weeek_token',
                    path: 'token',
                    component: WeeekSettingsToken,
                  },
                  {
                    name: 'import-weeek_statuses',
                    path: 'statuses',
                    component: WeeekStatuses,
                  },
                  {
                    name: 'import-weeek_priorities',
                    path: 'priorities',
                    component: WeeekPriorities,
                  },
                  {
                    name: 'import-weeek_types',
                    path: 'types',
                    component: WeeekTypes,
                  },
                  {
                    name: 'import-weeek_tags',
                    path: 'tags',
                    component: WeeekTags,
                  },
                ],
              },
            ],
          },
          {
            name: 'queue',
            path: 'namespace/:namespaceId/queue/:queueId',
            component: QueueLayout,
            redirect: { name: 'kanban' },
            meta: {
              middleware: [checkRoute],
            },
            props: (to) => {
              return {
                namespaceId: to.params.namespaceId,
              };
            },
            children: [
              {
                path: 'kanban',
                name: 'kanban',
                component: KanbanPage,
                props: (to) => {
                  return {
                    queueId: to.params.queueId,
                    projectId: Number(to.params.projectId),
                    namespaceId: to.params.namespaceId,
                  };
                },
              },
              {
                name: 'list',
                path: 'list',
                component: TirPmList,
              },
              {
                name: 'task-detail',
                path: 'task-detail/:taskId?',
                meta: {
                  middleware: [checkRoute],
                },
                component: TaskDetailPage,
                props: (to) => {
                  const tab = Number(to.query?.tab);
                  return {
                    taskId: to.params.taskId ? Number(to.params.taskId) : undefined,
                    queueId: to.params.queueId,
                    projectId: Number(to.params.projectId),
                    namespaceId: to.params.namespaceId,
                    tab: isNaN(tab) ? 0 : tab,
                  };
                },
              },
            ],
          },
          {
            name: 'namespace',
            path: 'namespace/:namespaceId',
            redirect: {
              name: 'namespace-queues',
            },
            meta: {
              middleware: [checkNamespaceAccess, checkRoute],
            },
            component: NamespacePage,
            children: [
              {
                name: 'namespace-queues',
                path: 'queues',
                component: NamespaceQueuesPage,
                props: (to) => {
                  return { namespaceId: to.params.namespaceId };
                },
              },
              {
                name: 'namespace-settings',
                path: 'settings',
                component: NamespaceMainSettingsPage,
                props: (to) => {
                  return { namespaceId: to.params.namespaceId };
                },
              },
              {
                name: 'namespace-types',
                path: 'types',
                component: NamespaceTypesSettingsPage,
                props: (to) => ({
                  namespaceId: to.params.namespaceId,
                }),
              },
              {
                name: 'namespace-difficulty',
                path: 'difficulty',
                component: NamespaceDifficultySettingsPage,
                props: (to) => {
                  return { namespaceId: to.params.namespaceId };
                },
              },
              {
                name: 'namespace-team',
                path: 'team',
                component: NamespaceTeamPage,
                props: (to) => {
                  return {
                    namespaceId: to.params.namespaceId,
                    projectId: Number(to.params.projectId),
                  };
                },
              },
              {
                name: 'namespace-admins',
                path: 'admins',
                component: NamespaceAdminsPage,
                props: (to) => {
                  return {
                    namespaceId: to.params.namespaceId,
                    projectId: Number(to.params.projectId),
                  };
                },
              },
            ],
          },
          {
            name: 'group',
            path: 'namespace/:namespaceId/group/:groupId',
            meta: {
              middleware: [checkRoute],
            },
            component: GroupPage,
            props: (to) => {
              return { groupId: Number(to.params.groupId), namespaceId: to.params.namespaceId };
            },
          },
        ],
      },
      {
        name: 'employee-tasks',
        path: 'employee-tasks/:employeeId',
        component: EmployeeTasksPage,
        props: (to) => {
          return {
            employeeId: Number(to.params.employeeId),
            projectId: Number(to.params.projectId),
          };
        },
      },
    ],
  },
];

export default routes;
